/**
 * SupportTicketDetailPage — Phase 34.10.
 *
 * Single ticket view with customer link and response body.
 */

import { Navigate, Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiBaseUrl } from '@/api/http';
import { getAccessToken } from '@/api/supabase';
import { fmtTime } from './CustomersDashboardPage';

// ── Types ────────────────────────────────────────────────────────────────────

interface TicketDetail {
  id: string;
  customer_id: string;
  subject: string;
  body: string;
  language: string;
  status: string;
  response_body: string | null;
  chunks_used: number;
  created_at: number;
  resolved_at: number | null;
}

interface ListTicketsResponse {
  tickets: TicketDetail[];
  total: number;
}

// ── Data ─────────────────────────────────────────────────────────────────────

async function fetchTicket(ticketId: string, customerId: string): Promise<TicketDetail | null> {
  const token = await getAccessToken();
  const resp = await fetch(`${apiBaseUrl()}/customers/${customerId}/tickets`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  const data = await resp.json() as ListTicketsResponse;
  return data.tickets.find(t => t.id === ticketId) ?? null;
}

// ── Sub-components ────────────────────────────────────────────────────────────

export const STATUS_COLORS: Record<string, string> = {
  open:      '#fbbf24',
  resolved:  '#34d399',
  escalated: '#f87171',
};

function Block({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>
        {label}
      </div>
      {children}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function SupportTicketDetailPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const { ticketId = '', customerId = '' } = useParams<{ ticketId: string; customerId: string }>();

  const { data: ticket, isPending, isError } = useQuery({
    queryKey: ['support', 'ticket', ticketId, customerId],
    queryFn: () => fetchTicket(ticketId, customerId),
    enabled: !!ticketId && !!customerId,
    refetchInterval: 30_000,
  });

  if (isPending) {
    return (
      <div className="omega-shell" style={{ fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
        <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>
      </div>
    );
  }

  if (isError || !ticket) {
    return (
      <div className="omega-shell" style={{ fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
        <div style={{ color: '#f87171', fontSize: '9pt' }}>Ticket not found.</div>
      </div>
    );
  }

  const statusColor = STATUS_COLORS[ticket.status] ?? 'var(--omega-ink-subtle)';

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs
        crumbs={[
          { label: 'OMEGA', to: '/omega' },
          { label: 'Customers', to: '/omega/customers' },
          { label: 'Support', to: '/omega/customers/support' },
          { label: ticket.subject || ticket.id.slice(0, 8) },
        ]}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, marginTop: 8 }}>
        <h1 style={{ margin: 0, fontSize: '12pt', fontWeight: 700, letterSpacing: '0.04em' }}>
          {ticket.subject || 'No subject'}
        </h1>
        <span style={{ fontSize: '7.5pt', padding: '1px 8px', border: `1px solid ${statusColor}`, color: statusColor, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 700, borderRadius: 3 }}>
          {ticket.status}
        </span>
      </div>

      <Block label="Metadata">
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <div>
            <span style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)' }}>Customer </span>
            <Link to={`/omega/customers/${ticket.customer_id}`} style={{ fontSize: '9pt', color: '#7dd3fc', textDecoration: 'none' }}>
              {ticket.customer_id.slice(0, 12)}…
            </Link>
          </div>
          <div>
            <span style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)' }}>Language </span>
            <span style={{ fontSize: '9pt', color: 'var(--omega-ink)' }}>{ticket.language}</span>
          </div>
          <div>
            <span style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)' }}>Submitted </span>
            <span style={{ fontSize: '9pt', color: 'var(--omega-ink)' }}>{fmtTime(ticket.created_at)}</span>
          </div>
          {ticket.resolved_at && (
            <div>
              <span style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)' }}>Resolved </span>
              <span style={{ fontSize: '9pt', color: '#34d399' }}>{fmtTime(ticket.resolved_at)}</span>
            </div>
          )}
          {ticket.chunks_used > 0 && (
            <div>
              <span style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)' }}>KB chunks used </span>
              <span style={{ fontSize: '9pt', color: 'var(--omega-ink)' }}>{ticket.chunks_used}</span>
            </div>
          )}
        </div>
      </Block>

      <Block label="Ticket Body">
        <div style={{ background: 'var(--omega-surface, rgba(255,255,255,0.04))', border: '1px solid var(--omega-rim, rgba(255,255,255,0.08))', borderRadius: 6, padding: '14px 16px', fontSize: '9.5pt', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
          {ticket.body}
        </div>
      </Block>

      {ticket.response_body && (
        <Block label="Response">
          <div style={{ background: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.2)', borderRadius: 6, padding: '14px 16px', fontSize: '9.5pt', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
            {ticket.response_body}
          </div>
        </Block>
      )}

      {ticket.status === 'open' && (
        <div style={{ marginTop: 16 }}>
          <Link
            to={`/omega/customers/support?status=open`}
            style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', textDecoration: 'none' }}
          >
            ← Back to open tickets
          </Link>
        </div>
      )}
    </div>
  );
}
